import { useNavigate, useParams } from 'react-router-dom';

import { CapabilityGate } from '../components/CapabilityGate';
import { usePerson } from '../features/persons/use-persons';
import { ColetaPanel } from '../features/structured-collection/ColetaPanel';
import { useColetaDraft } from '../features/structured-collection/use-coleta';
import { useConversation } from '../hooks/use-conversation';

import '../features/structured-collection/coleta-2b.css';

/**
 * ColetaEstruturadaPage — rota `/atendimento/conversation/:conversationId/coleta`
 * (Brief 2B T4 · ADR-144). A Coleta é EM-conversa: renderiza a silhueta blurred
 * da conversa (decisão de design #3) + o painel de coleta flutuante.
 *
 * Entrada: botão "Coleta" no header da Conversa em curso (gated
 * `structured-collection`). Gate da tela: a mesma capability. Get-or-create do
 * rascunho ativo via `useColetaDraft`.
 */
export function ColetaEstruturadaPage(): JSX.Element {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const back = (): void => {
    if (conversationId !== undefined) navigate(`/atendimento/conversation/${conversationId}`);
    else navigate('/inbox');
  };

  return (
    <CapabilityGate
      capability="structured-collection"
      fallback={
        <div className="coleta-page">
          <div
            className="coleta-panel"
            role="dialog"
            style={{ width: 'min(420px, calc(100% - 32px))' }}
          >
            <div className="coleta-panel__body">
              <p>Você não tem permissão para coletar dados estruturados neste tenant.</p>
              <button className="btn btn--secondary btn--sm" type="button" onClick={back}>
                Voltar à conversa
              </button>
            </div>
          </div>
        </div>
      }
    >
      <ColetaContent conversationId={conversationId} onBack={back} />
    </CapabilityGate>
  );
}

function ColetaContent({
  conversationId,
  onBack,
}: {
  conversationId: string | undefined;
  onBack: () => void;
}): JSX.Element {
  const { data: conversation, isLoading: convLoading } = useConversation(conversationId);
  const { draft, isLoading: draftLoading, isError, refetch } = useColetaDraft(conversationId);
  // D4 — detalhe da pessoa só p/ derivar o aviso read-only de capacidade jurídica.
  const { data: person } = usePerson(conversation?.personId ?? undefined);

  const loading = convLoading || draftLoading;

  return (
    <div className="coleta-page">
      <Silhouette />

      {loading && (
        <div
          className="coleta-panel"
          role="status"
          style={{ width: 'min(420px, calc(100% - 32px))' }}
        >
          <div className="coleta-panel__body">Carregando coleta…</div>
        </div>
      )}

      {!loading && (isError || draft === undefined) && (
        <div
          className="coleta-panel"
          role="alert"
          style={{ width: 'min(420px, calc(100% - 32px))' }}
        >
          <div className="coleta-panel__body">
            <p>Não foi possível abrir a coleta desta conversa.</p>
            <button className="btn btn--secondary btn--sm" type="button" onClick={onBack}>
              Voltar à conversa
            </button>
          </div>
        </div>
      )}

      {!loading && draft !== undefined && conversationId !== undefined && (
        <ColetaPanel
          conversationId={conversationId}
          draft={draft}
          bondId={conversation?.bondId ?? undefined}
          personName={conversation?.person?.displayName ?? null}
          person={person}
          cadastralChangeDetected={Boolean(conversation?.qualitySignals.cadastralChangeFlag)}
          onClose={onBack}
          onRefetch={refetch}
        />
      )}
    </div>
  );
}

/** Silhueta blurred decorativa (sem texto fabricado — só formas). */
function Silhouette(): JSX.Element {
  return (
    <div className="conv-bg" aria-hidden="true">
      <div className="conv-bg__main">
        <div className="conv-bg__header" />
        <div className="conv-bg__messages">
          <div className="conv-bg__bubble conv-bg__bubble--in" />
          <div className="conv-bg__bubble conv-bg__bubble--out" />
          <div className="conv-bg__bubble conv-bg__bubble--in" />
          <div className="conv-bg__bubble conv-bg__bubble--out" />
        </div>
        <div className="conv-bg__composer" />
      </div>
      <div className="conv-bg__side" />
    </div>
  );
}
